import React, { useState } from 'react'
import { ScrollView, View, ViewStyle, TouchableOpacity } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { NavigationContainer } from '@src/screens/Common/containers'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'
import { openUrl } from '@src/utils'
import { Analytics, CustomEventTypes, Device } from '@src/config'
import { useTutorialNavigation } from '@src/screens/Tutorials/hooks'
import { UiInlineAlert, UiStateNames } from '@src/types'
import { InlineAlert, LoadingIndicator } from '@src/components'
import { AppStackParamList } from '@src/navigation/types'
import { useIsDarkMode } from '@src/config/theme'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { terraAppleHealthActiveSelector } from '@src/selectors/integrations'
import { useCheckGlucoseConnection } from '../Connectivity/hooks/useCheckGlucoseConnection'
import {
  useByosOnboardingTutorial,
  useExitByosOnboarding,
  useGetThirdPartyAppName,
  useIsIndirectSyncSensorMode,
  useShouldShowByosOnboarding,
} from './hooks'

const HEALTH_APP_LABEL = Device.ios ? 'Apple Health' : 'Health Connect'

const APPLE_HEALTH_SUPPORT_URL =
  'https://support.nutrisense.io/hc/en-us/articles/4826896538903-How-to-Sync-your-HealthKit'
const HEALTH_CONNECT_SUPPORT_URL =
  'https://support.nutrisense.io/en_us/app-integrations-health-connect-formerly-google-fit-ryAbWb8igx'
const RETRY_MIN_LOADING_MS = 1200

export const CheckConnectionScreen = () => {
  const styles = useStyleSheet(themedStyles)
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(false)
  const [connectionFailed, setConnectionFailed] = useState(false)
  const [hasConnectionFailedOnce, setHasConnectionFailedOnce] = useState(false)
  const [alertVisible, setAlertVisible] = useState(false)
  const navigation = useNavigation()
  const terraAppleHealthActive = useSelector(terraAppleHealthActiveSelector)
  const route = useRoute<RouteProp<AppStackParamList, 'CheckConnection'>>()
  const fromTutorialId = route.params?.fromTutorialId
  const isOnboarding = useShouldShowByosOnboarding()
  const allowBackNavigation = isOnboarding ? false : true
  const isDarkMode = useIsDarkMode()

  useLazyScreenPreload('SupportHub')

  const tutorialSensorKind = useByosOnboardingTutorial()
  const navigateToTutorial = useTutorialNavigation(tutorialSensorKind)
  const exitByosOnboarding = useExitByosOnboarding()
  const checkGlucoseConnection = useCheckGlucoseConnection()
  const appName = useGetThirdPartyAppName()
  const isIndirectSyncSensorMode = useIsIndirectSyncSensorMode()

  const alertTextColor = isDarkMode ? 'theme.tooltip.textPrimary' : 'theme.text.primary'

  const onRestartTutorialPress = () => {
    if (!isOnboarding && fromTutorialId) {
      navigation.navigate('CMSTutorial', { tutorialId: fromTutorialId })
      return
    }
    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.OwnSensorTutorialCompleted,
        value: false,
      },
    })

    if (!tutorialSensorKind) {
      exitByosOnboarding()
      return
    }

    navigateToTutorial()
  }

  const onLearnMorePress = () => {
    const url = Device.ios ? APPLE_HEALTH_SUPPORT_URL : HEALTH_CONNECT_SUPPORT_URL
    return openUrl(url)
  }

  const onContinuePress = () => {
    setConnectionFailed(false)

    if (isOnboarding) {
      dispatch({
        type: 'app/upsertUiState',
        payload: {
          name: UiStateNames.OwnSensorOnboardingCompleted,
          value: true,
        },
      })
    }

    navigation.reset({
      index: 0,
      routes: [{ name: 'Drawer' }],
    })
  }

  const checkConnectionWithTimeout = () => {
    return new Promise<boolean>((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Connection timeout'))
      }, 10000)

      checkGlucoseConnection()
        .then((result) => {
          resolve(result)
        })
        .catch((error) => {
          reject(error)
        })
        .finally(() => {
          clearTimeout(timer)
        })
    })
  }

  const onCheckConnectionPress = async () => {
    const isRetryAttempt = connectionFailed
    const retryStartedAt = Date.now()

    setLoading(true)
    setConnectionFailed(false)
    setAlertVisible(false)

    try {
      const result = await checkConnectionWithTimeout()
      Analytics.track(CustomEventTypes.CheckGlucoseConnection, { success: result })

      if (result) {
        dispatch({
          type: 'app/upsertUiState',
          payload: {
            name: UiStateNames.OwnSensorGlucoseDetected,
            value: true,
          },
        })

        // Apple Health synced via Terra is ingested through Terra, so skip the
        // native push here to avoid writing the same measurements twice.
        if (!terraAppleHealthActive) {
          dispatch({
            type: 'app/updateHealthData',
          })
        }

        navigation.navigate('ConnectionSuccessful')
        return
      }

      // Add a small delay to reinforce that we are checking and avoid blinking
      if (isRetryAttempt) {
        const elapsed = Date.now() - retryStartedAt
        const remaining = Math.max(0, RETRY_MIN_LOADING_MS - elapsed)

        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining))
        }
      }

      setConnectionFailed(true)
      setHasConnectionFailedOnce(true)
      setAlertVisible(true)
    } catch {
      if (isRetryAttempt) {
        const elapsed = Date.now() - retryStartedAt
        const remaining = Math.max(0, RETRY_MIN_LOADING_MS - elapsed)

        if (remaining > 0) {
          await new Promise((resolve) => setTimeout(resolve, remaining))
        }
      }

      setConnectionFailed(true)
      setHasConnectionFailedOnce(true)
      setAlertVisible(true)
    } finally {
      setLoading(false)
    }
  }

  const onContactSupportPress = () => {
    navigation.navigate('SupportHub')
  }

  return (
    <NavigationContainer
      allowBackNavigation={allowBackNavigation}
      title="Let's check your connection"
    >
      <SafeAreaView edges={['bottom']} style={[styles.container]}>
        <ScrollView style={styles.contentScroll} contentContainerStyle={styles.contentContainer}>
          <View>
            <Text type="regular" bold style={styles.stepsTitle}>
              Steps completed so far
            </Text>
            <TouchableOpacity onPress={onLearnMorePress} accessibilityLabel="Learn more">
              <View style={styles.completedStep}>
                <Text style={styles.stepDescription} type="regular">
                  Accept permissions to sync {HEALTH_APP_LABEL}.
                </Text>
                <Text style={styles.stepLink} type="regular">
                  Learn more
                </Text>
              </View>
            </TouchableOpacity>
            <TouchableOpacity onPress={onRestartTutorialPress} accessibilityLabel="Learn more">
              <View style={styles.completedStep}>
                <Text style={styles.fullWidthStepDescription} type="regular">
                  {isIndirectSyncSensorMode
                    ? `Use a 3rd party app to import glucose data from your ${appName} app.`
                    : `Within the ${appName} app, enable syncing to ${HEALTH_APP_LABEL}.`}
                </Text>
              </View>
            </TouchableOpacity>
            {isIndirectSyncSensorMode && (
              <View style={styles.completedStep}>
                <Text
                  style={[styles.stepDescription, styles.fullWidthStepDescription]}
                  type="regular"
                >
                  In the 3rd party app, enable glucose exporting to {HEALTH_APP_LABEL}. From{' '}
                  {HEALTH_APP_LABEL}, glucose data will flow into Nutrisense.
                </Text>
              </View>
            )}

            <View style={styles.inlineNotice}>
              <Icon name="info" weight="regular" />
              <Text type="regular" style={styles.inlineNoticeText}>
                These steps are performed outside of our app, so we're assuming you have completed
                them already. Want to review? Press{' '}
                <Text type="regular" bold style={styles.stepLink} onPress={onRestartTutorialPress}>
                  here to restart the tutorial
                </Text>
                .
              </Text>
            </View>
          </View>
        </ScrollView>

        <View style={styles.buttonContainer}>
          {!loading && !connectionFailed && (
            <Button
              type="primary"
              size="block"
              onPress={onCheckConnectionPress}
              accessibilityLabel="Check Connection"
            >
              Check Connection
            </Button>
          )}
          {loading && !connectionFailed && !hasConnectionFailedOnce && (
            <Button
              type="primary"
              size="block"
              accessibilityLabel="Checking Connection"
              disabled={false}
              accessoryRight={<LoadingIndicator viewStyles={styles.loadingView} color="white" />}
              isAccessoryInline
            >
              Checking Connection
            </Button>
          )}

          {hasConnectionFailedOnce && (
            <>
              {connectionFailed && alertVisible && (
                <InlineAlert
                  style={[styles.warningPopup]}
                  textStyle={{ color: alertTextColor } as ViewStyle}
                  category={UiInlineAlert.Warning}
                  onClose={() => setAlertVisible(false)}
                  // eslint-disable-next-line max-len
                  message="No data detected. Note, it may take 3 hours for Nutrisense to receive the data. If you have just applied your sensor, please continue."
                />
              )}
              <Button
                type="primary"
                size="block"
                onPress={onContinuePress}
                style={styles.continueButton}
                accessibilityLabel="Continue"
                disabled={loading}
              >
                Continue
              </Button>
              <Button
                type="secondary"
                size="block"
                onPress={onCheckConnectionPress}
                accessibilityLabel="Retry"
                disabled={loading}
                accessoryRight={
                  loading ? (
                    <LoadingIndicator viewStyles={styles.loadingView} color="white" />
                  ) : null
                }
                isAccessoryInline={loading}
              >
                {loading ? 'Retrying...' : 'Retry'}
              </Button>
            </>
          )}
        </View>
        {!isOnboarding && hasConnectionFailedOnce && (
          <Text type="regular" style={styles.footer}>
            Still have issues? Contact our{' '}
            <TouchableOpacity accessibilityLabel="Contact Support" onPress={onContactSupportPress}>
              <Text type="regular" bold lineSpacing="none" style={styles.link}>
                Support Team
              </Text>
            </TouchableOpacity>{' '}
            to help you further diagnose!
          </Text>
        )}
      </SafeAreaView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
    marginTop: 18,
  },
  contentContainer: {
    flexGrow: 1,
    alignItems: 'center',
    paddingHorizontal: 16,
    backgroundColor: 'theme.background',
  },
  contentScroll: {
    flex: 1,
  },
  buttonContainer: {
    marginHorizontal: 24,
    marginBottom: 16,
  },
  continueButton: {
    marginBottom: 8,
  },
  warningPopup: {
    marginVertical: 8,
    padding: 8,
    backgroundColor: 'theme.warning.base',
  },
  warningTextStyle: {
    color: 'theme.tooltip.textPrimary',
  },
  completedStep: {
    flexDirection: 'row',
    minWidth: '100%',
    backgroundColor: 'theme.surface.base3',
    marginTop: 16,
    padding: 16,
    borderRadius: 16,
    justifyContent: 'space-between',
  },
  stepDescription: {
    flexWrap: 'wrap',
    maxWidth: 184,
  },
  stepsTitle: {
    marginBottom: 8,
  },
  fullWidthStepDescription: {
    maxWidth: '100%',
    flex: 1,
  },
  stepLink: {
    fontWeight: 'bold',
    alignSelf: 'center',
    color: 'theme.text.link',
  },
  inlineNotice: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 8,
  },
  inlineNoticeText: {
    alignSelf: 'center',
    maxWidth: 312,
  },
  loadingView: {
    height: 20,
    marginLeft: 4,
  },
  footer: {
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 8,
    marginHorizontal: 16,
  },
  link: {
    color: 'theme.text.link',
    marginTop: 'auto',
  },
})
